import React from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'subtle' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 font-medium rounded-md transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-mid/60 focus-visible:ring-offset-2 focus-visible:ring-offset-paper ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<ButtonVariant, string> = {
  // Primario: verde campo profundo, sólido (sin gradientes). `mcd-btn-primary`
  // habilita el gradiente solo en modo colorido (ver index.css).
  primary: 'mcd-btn-primary bg-forest text-paper hover:bg-forest-mid',
  // Secundario: papel con filete (acción secundaria sobre fondo claro).
  secondary: 'bg-paper-2 text-ink border border-hairline hover:bg-hairline/50',
  // Fantasma: transparente, para acciones terciarias en espacios reducidos.
  ghost: 'text-ink hover:bg-paper-2',
  // Sutil: verde translúcido (alternativa suave al primario).
  subtle: 'bg-forest/10 text-forest hover:bg-forest/15',
  // Peligro: terracota (borrados, acciones destructivas).
  danger: 'bg-danger text-paper hover:brightness-110',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'text-sm px-3 py-1.5',
  md: 'text-sm px-4 py-2.5',
  lg: 'text-base px-5 py-3.5',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
}

/**
 * Botón base del design system (manual de marca): primario Verde Campo,
 * foco en Verde Pastura.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading = false, fullWidth = false, className, children, disabled, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
          <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

export default Button;
